import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { get, isEmpty } from "lodash";
import { withTranslation, useTranslation } from "react-i18next";
import validator from "validator";
import { usePreCheckInContext } from "./contexts/PreCheckInContext";
import { ProgressBar } from "./GuestInfoStyle";
import FirstStep from "./GuestSteps/FirstStep";
import SecondStep from "./GuestSteps/SecondStep";
import ThirdStep from "./GuestSteps/ThirdStep";
import BookingValidation from "./BookingValidation";
import ReservationService from "../../backend/ReservationService";
import { ClipLoaderIcon } from "../../components/LoadingIcon";

const GuestInfo = ({ match }) => {
  const initialFormState = {
    title: "Mr",
    firstName: "",
    lastName: "",
    email: "",
    country: "",
    countryCode: "",
    nationality: "",
    nationalityCode: "",
    phoneCode: "0",
    phone: "",
    birthday: "",
    passport: "",
    dateOfExpiry: "",
    tmSix: "",
    document: "",
    signature: "",
    clientSecret: "",
    stripeSubmitted: false,
    verified: false,
    idType: "",
    address: null,
    terms: [],
    completed: false,
  };

  const {
    propertyUid,
    bookingNumber,
    lastName,
    userEmail,
    language,
    data: {
      accessToken,
      guestService,
      configuration,
      presets,
      booking,
      guests,
      property,
    },
    components: { PreCheckInContainer },
    func: { updateGuests, useFormInput, setAccessTokenData, updateErrorPage },
  } = usePreCheckInContext();

  const history = useHistory();
  const { t } = useTranslation();
  const [formData, handleFormChange, setFormValues] =
    useFormInput(initialFormState);
  const [isPageMounted, setIsPageMounted] = useState(true);
  const [guestNumber, setGuestNumber] = useState(1);
  const [allTerms, setAllTerms] = useState([]);
  const [step, setStep] = useState(1);
  const [preStep, setPreStep] = useState(0);
  const [primaryGuest, setPrimaryGuest] = useState(false);
  const [globalError, setGlobalError] = useState(null);
  const [errorsValidation, setErrorsValidation] = useState({});
  const [currentGuest, setCurrentGuest] = useState({});
  const [loading, setLoading] = useState(false);

  const getTerms = useCallback(() => {
    let allTerms = [],
      requiredTerms = [];
    if (configuration && configuration.optInTermsTitle.length > 0) {
      allTerms.push({
        name: "optInTerms",
        defaultText: configuration.optInTermsTitle,
      });
    }

    const param_1 = t("BY_SUBMIT_FORM_1");
    const param_2 = t("BY_SUBMIT_FORM_2");
    const param_3 = t("BY_SUBMIT_FORM_3");
    const param_4 = t("BY_SUBMIT_FORM_4");
    const regexForm = [param_2, param_3, param_4];

    let title = [t("PRIVACY_STATEMENT")];
    let link = [guestService.privacyStatementLink || "https://www.okkami.com/privacy-policy"];
    if (["GHA", "Cendyn"].includes(presets.appAuthenticationType)) {
      title.push(t("PRIVACY_POLICY"));
      link.push(guestService.privacyPolicyLink || "https://www.okkami.com/privacy-policy");
    }

    guestService.guestsTerms.forEach((term, index) => {
      let name = `terms${index + 1}`;
      allTerms.push({
        name,
        preTitle: get(term, "pre_text"),
        title: get(term, "link_label_title"),
        popupText: get(term, "popup_text"),
      });
      requiredTerms[name] = "";
    });

    let terms0 = { regexForm };
    if (!isEmpty(param_1)) {
      terms0 = {
        name: "terms0",
        title: param_1,
        ...terms0,
      };
      requiredTerms["terms0"] = "";
    }
    allTerms.push(terms0);
    setAllTerms(allTerms);
    return requiredTerms;
  }, [configuration, guestService, presets, t]);

  useEffect(() => {
    const { step: matchedStep, guestNumber: matchedGuestNumber } = match.params;
    setStep(parseInt(matchedStep));
    setGuestNumber(matchedGuestNumber);
    setPrimaryGuest(matchedGuestNumber === "1");

    if (guestService && guests && isPageMounted) {
      setIsPageMounted(false);
      const currGuest = guests.allGuests()[matchedGuestNumber - 1];
      setCurrentGuest(currGuest);

      const guestData = {
        title: currGuest.title || "Mr",
        firstName: currGuest.firstName,
        lastName: currGuest.lastName,
        email: currGuest.email,
        country: currGuest.country,
        countryCode: currGuest.countryCode,
        nationality: currGuest.nationality,
        nationalityCode: currGuest.nationalityCode,
        phoneCode: currGuest.phoneCode,
        phone: currGuest.phone,
        birthday: currGuest.birthday,
        passport: currGuest.passport,
        dateOfExpiry: currGuest.dateOfExpiry,
        tmSix: currGuest.tmSix,
        document: currGuest.document,
        signature: currGuest.signature,
        clientSecret: currGuest.clientSecret,
        idType: currGuest.idType,
        stripeSubmitted: currGuest.stripeSubmitted,
        verified: currGuest.verified,
        address: currGuest.address,
        terms: getTerms(),
      };
      setFormValues({ ...formData, ...guestData });
    }
  }, [
    match.params,
    propertyUid,
    isPageMounted,
    guests,
    formData,
    setFormValues,
    getTerms,
    guestService,
  ]);

  useEffect(() => {
    if (configuration) getTerms();
  }, [configuration, getTerms]);

  const goBack = () => {
    const currGuest = guests.allGuests()[guestNumber - 1];
    if (currGuest.clientSecret) {
      history.push(
        `/pre-checkin/${propertyUid}/book-detail/${bookingNumber}/guests/${guestNumber}/confirm?language=${language}`
      );
    } else {
      goToBookingDetail();
    }
  };

  const goToPreferences = () => {
    history.push(
      `/pre-checkin/${propertyUid}/book-detail/${bookingNumber}/guests/${guestNumber}/preferences?language=${language}`
    );
  };

  const goToBookingDetail = () => {
    history.push(
      `/pre-checkin/${propertyUid}/book-detail/${bookingNumber}${
        lastName ? `/${encodeURIComponent(lastName)}` : ""
      }?language=${language}${
        userEmail ? `&email=${encodeURIComponent(userEmail)}` : ""
      }`
    );
  };

  const nextStep = (newStep = null, newPreStep = null) => {
    let newPreStepUpdated = newPreStep === null ? preStep : newPreStep;
    const nextStep = newStep === null ? step + 1 : newStep;
    if (newPreStepUpdated >= nextStep - 1 || newStep < step) {
      setStep(nextStep);
      setPreStep(newPreStepUpdated);
      history.push(
        `/pre-checkin/${propertyUid}/book-detail/${bookingNumber}/guests/${guestNumber}/steps/${nextStep}?language=${language}`
      );
    }
  };

  const handleValidation = (fields) => {
    let errors = {};
    let globalError = null;
    fields.forEach((field) => {
      if (field["validates"]) {
        if (field["validates"].split("|").includes("required")) {
          if (
            Array.isArray(formData[field["name"]]) ||
            formData[field["name"]] instanceof Object
          ) {
            for (const [key, value] of Object.entries(
              formData[field["name"]]
            )) {
              if (!value) {
                errors[key] = [];
                errors[key].push(
                  t(
                    "PLEASE_FILL_OUT_REQUIRED",
                    "Please fill all the required fields"
                  )
                );
              }
            }
          } else if (validator.isEmpty(formData[field["name"]])) {
            errors[field["name"]] = [];
            errors[field["name"]].push(
              t(
                "PLEASE_FILL_OUT_REQUIRED",
                "Please fill all the required fields"
              )
            );
          }
        }

        // eslint-disable-next-line no-control-regex
        const rForeign = new RegExp("[^\u0000-\u007f]");
        if (
          formData[field["name"]] &&
          rForeign.test(formData[field["name"]]) &&
          !guestService.enableNonLatinChar
        ) {
          globalError = t(
            "REQUIRED_ENGLISH_TEXT",
            "Required fields - Please fill in with English language"
          );
        }

        if (
          field["name"] === "email" &&
          field["validates"].split("|").includes("email")
        ) {
          if (guestService.allowWithoutEmail) {
            delete errors["email"];
          } else if (
            formData[field["name"]] &&
            !validator.isEmail(formData[field["name"]])
          ) {
            if (errors[field["name"]] === undefined) {
              errors[field["name"]] = [];
            }
            errors[field["name"]].push(
              t("INVALID_EMAIL_GUEST", "Please enter a valid email")
            );
          }

          if (
            !primaryGuest &&
            [...guests.allGuests()].some(
              (guest, index) =>
                index !== guestNumber - 1 && guest?.email === formData.email
            )
          ) {
            if (!errors[field["name"]]) errors[field["name"]] = [];
            errors[field["name"]].push(
              t(
                "ERROR_EMAIL_DUPLICATED_GUEST",
                "Error: The email is duplicated with another guest"
              )
            );
          }
        }
      }
    });
    setErrorsValidation(errors);
    setGlobalError(globalError);
    return Object.keys(errors).length === 0 && globalError === null;
  };

  const goToNextStep = async () => {
    if (handleValidation(requiredFields())) {
      window.scrollTo(0, 0);
      if (step === 3 || (step === 2 && guestService.skipAllPage)) {
        saveCurrentGuest(true);
        if (guestService.enablePreferenceCollection) {
          if (isEmpty(currentGuest.profileId) && !presets.demoMode) {
            requestCreateAdditionalGuest(prepareAdditionalGuestData());
          } else {
            goToPreferences();
          }
        } else goBack();
      } else {
        saveCurrentGuest();
        nextStep(step + 1, step);
      }
    }
  };

  const saveCurrentGuest = (completed = false) => {
    const guestData = {
      ...currentGuest,
      title: formData.title,
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      country: formData.country,
      countryCode: formData.countryCode,
      nationality: formData.nationality,
      nationalityCode: formData.nationalityCode,
      phoneCode: formData.phoneCode,
      phone: formData.phone,
      birthday: formData.birthday,
      passport: formData.passport,
      dateOfExpiry: formData.dateOfExpiry,
      tmSix: formData.tmSix,
      document: formData.document,
      signature: formData.signature,
      clientSecret: formData.clientSecret,
      stripeSubmitted: formData.stripeSubmitted,
      verified: formData.verified,
      idType: formData.idType,
      address: formData.address,
      completed: completed,
    };
    setCurrentGuest(guestData);
    guests.UpdateByNumber(guestNumber - 1, guestData);
    updateGuests(guests);
  };

  const requiredFields = () => {
    let fieldNames = BookingValidation().steps[step - 1];
    let exclude = [];
    if (!guestService.enablePassport) {
      exclude.push("country", "nationality", "passport", "document");
    }
    if (!guestService.enableTM6) {
      exclude.push("tmSix");
    }
    if (!guestService.enablePassportExpiration) {
      exclude.push("dateOfExpiry");
    }
    if (!guestService.phoneRequired) {
      exclude.push("phoneCode");
      exclude.push("phone");
    }

    if (guestService.skipAllPage && step !== 1) {
      fieldNames.push("signature");
    }

    if (!guestService.enableSignature || !guestService.requiredSignature) {
      exclude.push("signature");
    }

    if (guestService.enableDateOfBirth && guestService.requiredDateOfBirth) {
      fieldNames["birthday"] = [];
      fieldNames["birthday"].push(t("PLEASE_FILL_OUT_REQUIRED"));
    }

    if (!guestService.enableDateOfBirth || !guestService.requiredDateOfBirth) {
      exclude.push("birthday");
    }

    fieldNames = fieldNames.filter((e) => !exclude.includes(e));
    return BookingValidation().fields.filter((field) =>
      fieldNames.includes(field["name"])
    );
  };

  const disabledPrimaryGuest = (email) => {
    if (email) {
      for (const index in presets.otaEmailList) {
        const otaEmailRegex = presets.otaEmailList[index];
        const emailRegex = get(otaEmailRegex, "regex");
        if (emailRegex) {
          const regex = new RegExp(emailRegex);
          if (email.match(regex)) {
            return false;
          }
        }
      }
    }
    return true;
  };

  const options = [
    { value: "Mr", label: t("MR", "Mr") },
    { value: "Mrs", label: t("MRS", "Mrs") },
    { value: "Ms", label: t("MS", "Ms") },
    { value: "Miss", label: t("MISS", "Miss") },
  ];

  const goToAddressCollection = () => {
    history.push(
      `/pre-checkin/${propertyUid}/book-detail/${bookingNumber}/guests/${guestNumber}/address-collection?language=${language}`
    );
  };

  const prepareAdditionalGuestData = () => {
    const reservationId = get(booking, "resv_id");
    const bookingId = get(booking, "booking_id");
    const data = {
      reservation_id: reservationId,
      booking_id: bookingId,
      property_uid: propertyUid,
      guest_details: {
        first_name: currentGuest.firstName,
        last_name: currentGuest.lastName,
        email: currentGuest.email,
        dob: currentGuest.birthday,
        nationality_code: currentGuest.nationalityCode,
        address: null,
      },
    };
    return data;
  };
  const requestCreateAdditionalGuest = async (newChildrenData) => {
    setLoading(true);
    window.scrollTo(0, 0);
    await ReservationService.createAdditionalGuest(
      accessToken,
      propertyUid,
      newChildrenData
    )
      .then((response) => {
        const guestData = {
          ...currentGuest,
          guestId: get(response.data, "guest_details.guest_id"),
          profileId: get(response.data, "guest_details.profile_id"),
          preferences: get(response.data, "guest_details.preferences"),
        };
        setCurrentGuest(guestData);
        guests.UpdateByNumber(guestNumber - 1, guestData);
        updateGuests(guests);
        goToPreferences();
      })
      .catch((error) => {
        if (get(error.response, "status") === 401) {
          setAccessTokenData();
        }
        console.error(error);
        updateErrorPage(true);
      });
  };

  return (
    <>
      <PreCheckInContainer
        containerName="guest-info"
        backToHome>
        {booking && (
          <>
            {!guestService.enablePreferenceCollection && (
              <section
                id="header"
                className="w-full sticky top-0 z-10">
                <ProgressBar
                  backgroundColorTopBar={presets.backgroundColorTopBar}
                  appTopNavBgColor={presets.appTopNavBgColor}
                  appHomeTextTopNav={presets.appHomeTextTopNav}
                  appHomeTopNavIconBackground={
                    presets.appHomeTopNavIconBackground
                  }
                  appHomeTopNavIconColor={presets.appHomeTopNavIconColor}
                  className="flex pt-6 py-6 px-4 text-left cursor-pointer preCheckinTitle font-bold text-14 progressbar">
                  <li
                    className="w-1/4 active"
                    onClick={() => goBack()}></li>
                  <li
                    className={`w-1/4 active ${step > 1 && "tick"}`}
                    onClick={() => nextStep(1)}></li>
                  <li
                    className={`w-1/4 ${step > 2 && "tick"} ${
                      step > 1 && "active"
                    }`}
                    onClick={() => nextStep(2)}></li>
                  <li className={`w-1/4 ${step > 2 && "active"}`}></li>
                </ProgressBar>
              </section>
            )}

            <section
              id="content"
              className="relative flex flex-col px-9 w-full flex-1 md:flex-0 pt-6">
              <div className="md:max-w-7xl mx-auto w-full">
                {step === 1 && (
                  <FirstStep
                    options={options}
                    title={guestService.basicInformationDescription}
                    titleColor={presets.titleColor}
                    subtitleColor={presets.subtitleColor}
                    backgroundColorTopBar={presets.backgroundColorTopBar}
                    getTitle={formData.title}
                    globalError={globalError}
                    getFirstName={formData.firstName}
                    getLastName={formData.lastName}
                    getEmail={formData.email}
                    errors={errorsValidation}
                    disabled={
                      guestService.preventPrimaryGuestEditing && primaryGuest
                    }
                    disabledEmail={
                      (booking.email &&
                        primaryGuest &&
                        guestService.preventPrimaryGuestEditing &&
                        disabledPrimaryGuest(booking.email)) ||
                      (primaryGuest &&
                        guestService.preventPrimaryGuestEmailEditing)
                    }
                    handleChange={handleFormChange}
                    allowWithoutEmail={guestService.allowWithoutEmail}
                  />
                )}

                {step === 2 && (
                  <SecondStep
                    title={guestService.personalInformationDescription}
                    enableAddressCollection={
                      guestService.enableAddressCollection
                    }
                    requiredAddressCollection={
                      guestService.requiredAddressCollection
                    }
                    titleColor={presets.titleColor}
                    subtitleColor={presets.subtitleColor}
                    backgroundColorTopBar={presets.backgroundColorTopBar}
                    appHomeTextCallToAction={presets.appHomeTextCallToAction}
                    buttonStyleName={presets.buttonStyleName}
                    getCountry={formData.country}
                    getCountryCode={formData.countryCode}
                    getNationality={formData.nationality}
                    getNationalityCode={formData.nationalityCode}
                    getPhoneCode={formData.phoneCode}
                    getPhone={formData.phone}
                    phoneRequired={guestService.phoneRequired}
                    getBirthday={formData.birthday}
                    allTerms={allTerms}
                    terms={formData.terms}
                    errors={errorsValidation}
                    handleChange={handleFormChange}
                    enablePassport={guestService.enablePassport}
                    combination={guestService.skipAllPage}
                    getSignature={formData.signature}
                    getAddress={formData.address}
                    globalError={globalError}
                    goToAddressCollection={goToAddressCollection}
                    interfaceTypeIsHotelKey={
                      configuration.checkPreCheckInInterfaceTypeIsHotelKey
                    }
                    enableSignature={guestService.enableSignature}
                    requiredSignature={guestService.requiredSignature}
                    enableDateOfBirth={guestService.enableDateOfBirth}
                    requiredDateOfBirth={guestService.requiredDateOfBirth}
                    property={property}
                  />
                )}

                {step === 3 && (
                  <ThirdStep
                    title={guestService.documentCollectionDescription}
                    titleColor={presets.titleColor}
                    subtitleColor={presets.subtitleColor}
                    backgroundColorTopBar={presets.backgroundColorTopBar}
                    appHomeTextCallToAction={presets.appHomeTextCallToAction}
                    buttonStyleName={presets.buttonStyleName}
                    getPassport={formData.passport}
                    getDateOfExpiry={formData.dateOfExpiry}
                    getTmSix={formData.tmSix}
                    getDocument={formData.document}
                    getSignature={formData.signature}
                    errors={errorsValidation}
                    enableTM6={formData.enableTM6}
                    enablePassport={guestService.enablePassport}
                    enablePassportExpiration={
                      guestService.enablePassportExpiration
                    }
                    globalError={globalError}
                    // enableStripe={enableStripe}
                    handleChange={handleFormChange}
                    enableSignature={guestService.enableSignature}
                    requiredSignature={guestService.requiredSignature}
                  />
                )}
                <button
                  className={`app-button-ipad type-title medium ${presets.buttonStyleName}`}
                  style={{
                    backgroundColor: presets.backgroundColorTopBar,
                    color: presets.appHomeTextCallToAction,
                  }}
                  onClick={() => goToNextStep()}>
                  {t("CONTINUE")}
                </button>
              </div>
            </section>
            <button
              className="app-button-mobile type-title medium "
              style={{
                backgroundColor: presets.backgroundColorTopBar,
                color: presets.appHomeTextCallToAction,
                position: "fixed",
                bottom: 0,
                left: 0,
              }}
              onClick={() => goToNextStep()}>
              {t("CONTINUE")}
            </button>
            {loading && (
              <div className="fixed inset-0 flex justify-center items-center bg-gray-500 opacity-50 z-50">
                <ClipLoaderIcon
                  loadingState={loading}
                  size={30}
                  color={presets.backgroundColorTopBar}
                />
              </div>
            )}
          </>
        )}
      </PreCheckInContainer>
    </>
  );
};

export default withTranslation()(GuestInfo);
